import ApiFactory from 'services/api-factory'

import {
  IPromptAnalyzerRequestBody,
  IPromptAnalyzerResponse,
  IVideo
} from './interfaces'
import {
  IGenAiProjectResponse,
  IGenAiVideoUrlsResponse,
  ICommonGenerationRequestBody,
  IVideoStoryGenerationRequestBody,
  IResponse,
  IAiImageStyleItem,
  IAiVideoModel
} from './interfaces'
import {
  HD_720_QUALITY,
  VIDEO_API_PATH,
  REC_API_PATH,
  AI_IMAGE_API_PATH,
  GENERATE_VIDEO_MOCKS,
  AI_VIDEO_API_PATH
} from './consts'
import { getScenesCount } from './helpers'
import { logError } from 'utils/logger'

//ai-image-api
const AIImageApi = new ApiFactory({
  baseUrl: AI_IMAGE_API_PATH
})

export const getStyles = () =>
  AIImageApi.get<IAiImageStyleItem[]>(`/image-styles`)

//ai-video-api
const AiVideoApi = new ApiFactory({
  baseUrl: AI_VIDEO_API_PATH
})

export const getAiVideoModelsApi = () =>
  AiVideoApi.get<IAiVideoModel[]>(`/models?origin=gen-ai-flow`)

//recommendation-api
const GenerateApi = new ApiFactory({ baseUrl: REC_API_PATH })

const analyzePromptApi = (data: IPromptAnalyzerRequestBody) =>
  GenerateApi.post<IPromptAnalyzerResponse, IPromptAnalyzerRequestBody>(
    '/gen-ai/prompt-analyzer',
    data
  )

const getGenerateVideoStoryByDurationApi = (
  data: IVideoStoryGenerationRequestBody
) =>
  GenerateApi.post<IResponse, IVideoStoryGenerationRequestBody>(
    '/gen-ai/generate-video-story-by-duration',
    data
  )

const getGenerateImageStoryByDurationApi = (
  data: ICommonGenerationRequestBody
) =>
  GenerateApi.post<IResponse, ICommonGenerationRequestBody>(
    '/gen-ai/generate-image-story-by-duration',
    data
  )

//video-api
const VideoApi = new ApiFactory({
  baseUrl: VIDEO_API_PATH
})
const renderGenAiVideo = (videoId: number) =>
  VideoApi.post<
    void,
    {
      quality: number
    }
  >(`/video-projects/${videoId}/render`, {
    quality: HD_720_QUALITY
  })

export const getGenAiProjectDataApi = (id: number) =>
  VideoApi.get<IGenAiProjectResponse>(`/video-projects/${id}`)

export const getGenAiVideoUrlsApi = (uid: string) =>
  VideoApi.get<IGenAiVideoUrlsResponse>(`/video-projects/${uid}/video-urls`)

const analyzePrompt = async (
  prompt: string,
  enhanceScript: boolean,
  isVoiceOverDisabled: boolean,
  styleId: number | undefined
) => {
  const body: IPromptAnalyzerRequestBody = {
    enhanceScript,
    isVoiceOverDisabled,
    prompt,
    styleId
  }
  const response = await analyzePromptApi(body)
  return response.data
}

export const generateVideo = async (
  video: IVideo,
  models: IAiVideoModel[]
): Promise<string | null> => {
  const {
    mode,
    generateVideoBody: { styleId, prompt, isVoiceOverDisabled, model }
  } = video

  const isAiChoice = styleId === 0

  // If styleId is 0 (Ai Choice), call analyzePrompt to get the recommended styleId
  const analyzePromptResponse = await analyzePrompt(
    prompt,
    GENERATE_VIDEO_MOCKS.enhanceScript,
    isVoiceOverDisabled,
    isAiChoice ? undefined : styleId
  )

  const commonRequestBody: ICommonGenerationRequestBody = {
    prompt,
    styleId: analyzePromptResponse.styleId,
    enhanceScript: GENERATE_VIDEO_MOCKS.enhanceScript,
    scenesCount: getScenesCount(models, analyzePromptResponse),
    aspectRatio: GENERATE_VIDEO_MOCKS.aspectRatio,
    isVoiceOverDisabled
  }

  try {
    if (mode === 'storyboard') {
      const response =
        await getGenerateImageStoryByDurationApi(commonRequestBody)
      return response.data.genAiVideoHash
    }
    if (mode === 'animated') {
      const response = await getGenerateVideoStoryByDurationApi({
        ...commonRequestBody,
        // model is guaranteed to be present for 'animated' mode
        model: model as string
      })
      return response.data.genAiVideoHash
    }
  } catch (error) {
    logError(error)
    return null
  }

  return null
}

export const renderGenAiVideoApi = async (videoId: number) => {
  await renderGenAiVideo(videoId)
}
